import type { Classroom, DrawAction, Student } from "@classroom/domain";
import { useEffect, useRef, useState } from "react";
import { availableStudentIds, type DrawState, hongKongDate } from "./drawRounds";
import { useSaveBeforeUnload } from "./useSaveBeforeUnload";

export function useResponseStudentDraw({
  classroom,
  students,
  enabled,
  readOnly,
  onSave,
}: {
  classroom?: Classroom;
  students: Student[];
  enabled: boolean;
  readOnly: boolean;
  onSave: (classId: string, action: DrawAction) => Promise<DrawState>;
}) {
  const activeIds = students.filter((student) => student.active).map((student) => student.id);
  const scopeKey = `${classroom?.id}:${activeIds.join("|")}`;
  const [dateKey, setDateKey] = useState(hongKongDate);
  // Both entrances read the class's saved round and use the same atomic draw action.
  const remainingIds = availableStudentIds(activeIds, classroom?.drawState, dateKey);
  const remainingKey = remainingIds.join("|");
  const [selectionId, setSelectionId] = useState<string>();
  const [previewId, setPreviewId] = useState<string>();
  const [drawing, setDrawing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const animating = useRef(false);
  const pending = useRef(false);
  useSaveBeforeUnload(saving);

  useEffect(() => {
    if (!enabled) return;
    setDateKey(hongKongDate());
    const interval = window.setInterval(() => setDateKey(hongKongDate()), 30_000);
    return () => window.clearInterval(interval);
  }, [enabled]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: A different view, roster or day invalidates the displayed selection.
  useEffect(() => {
    setSelectionId(undefined);
    setSaveFailed(false);
  }, [enabled, readOnly, scopeKey, dateKey]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Cancel an uncommitted animation when its view or candidate round changes.
  useEffect(() => {
    window.clearTimeout(timer.current);
    animating.current = false;
    setPreviewId(undefined);
    setDrawing(false);
    return () => {
      window.clearTimeout(timer.current);
      animating.current = false;
    };
  }, [enabled, readOnly, scopeKey, dateKey, remainingKey]);

  const result = classroom?.drawState?.history?.find(
    (entry) =>
      entry.id === selectionId && activeIds.includes(entry.studentId) && !remainingIds.includes(entry.studentId),
  );

  async function save(action: DrawAction) {
    if (!enabled || readOnly || !classroom || pending.current) return;
    pending.current = true;
    setSaving(true);
    setSaveFailed(false);
    try {
      const next = await onSave(classroom.id, action);
      if (action.type === "draw" && next.history?.some((entry) => entry.id === action.entryId)) {
        setSelectionId(action.entryId);
      }
    } catch {
      setSelectionId(undefined);
      setSaveFailed(true);
    } finally {
      setPreviewId(undefined);
      pending.current = false;
      setSaving(false);
    }
  }

  function draw() {
    if (!enabled || readOnly || !remainingIds.length || animating.current || pending.current) return;
    const studentId = remainingIds[Math.floor(Math.random() * remainingIds.length)];
    if (!studentId) return;
    setSelectionId(undefined);
    setSaveFailed(false);
    const finish = () => {
      animating.current = false;
      setDrawing(false);
      setPreviewId(studentId);
      void save({ type: "draw", studentId, entryId: crypto.randomUUID(), drawnAt: new Date().toISOString() });
    };
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      finish();
      return;
    }
    animating.current = true;
    setDrawing(true);
    let frame = 0;
    const advance = () => {
      setPreviewId(remainingIds[Math.floor(Math.random() * remainingIds.length)]);
      frame += 1;
      // Slow down before revealing the independently selected, uniform result.
      timer.current = window.setTimeout(frame < 12 ? advance : finish, frame < 12 ? 60 + frame * 18 : 260);
    };
    advance();
  }

  function reset() {
    if (animating.current || pending.current) return;
    setSelectionId(undefined);
    void save({ type: "reset", date: hongKongDate() });
  }

  return {
    draw,
    reset,
    result,
    drawing,
    saving,
    saveFailed,
    remainingIds,
    highlightedId: previewId || result?.studentId,
  };
}
